import React, { useEffect, useMemo, useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast, ToastContainer } from "react-toastify";
import {
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { Link } from "react-router-dom";

import Spinners from "components/Common/Spinner";
import DeleteModal from "components/Common/DeleteModal";
import TableContainer from "components/Common/TableContainer";
import Breadcrumbs from "components/Common/Breadcrumb";

import {
  fetchKelasOnline,
  createKelasOnline,
  updateKelasOnline,
  deleteKelasOnline,
} from "services/kelasOnlineService";

const KelasOnline = () => {
  document.title = "Kelas Online | [Nama Aplikasi]";

  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentKelas, setCurrentKelas] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  });
  const [deleteModal, setDeleteModal] = useState(false);

  // --- API Interaction Functions ---

  const fetchData = async (page = pagination.page, per_page = pagination.per_page) => {
    try {
      setLoading(true);
      const response = await fetchKelasOnline({ page, per_page });
      setData(response.data || []);
      setPagination({
        page: response.meta?.page || 1,
        per_page: response.meta?.per_page || 10,
        total: response.meta?.total || 0,
        last_page: response.meta?.last_page || 1,
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal memuat data kelas online.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- Modal and Form Handlers ---

  const toggleModal = () => {
    setModal(!modal);
    if (modal) {
      setCurrentKelas(null);
      setIsEdit(false);
      validation.resetForm();
    }
  };

  const handleEditClick = (kelas) => {
    setCurrentKelas(kelas);
    setIsEdit(true);
    toggleModal();
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: currentKelas?.title || "",
      class_date: currentKelas?.class_date 
        ? new Date(currentKelas.class_date).toISOString().split('T')[0] 
        : "",
      start_time: currentKelas?.start_time || "",
      end_time: currentKelas?.end_time || "",
      url: currentKelas?.url || "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Nama kelas wajib diisi"),
      class_date: Yup.string().required("Tanggal kelas wajib diisi"),
      start_time: Yup.string().required("Jam mulai wajib diisi"),
      end_time: Yup.string()
        .required("Jam selesai wajib diisi")
        .test(
          'is-greater',
          'Jam selesai harus lebih besar dari jam mulai',
          function(value) {
            const { start_time } = this.parent;
            if (!start_time || !value) {
              return true;
            }
            return value > start_time;
          }
        ),
      url: Yup.string().url("Harus berupa link yang valid").required("Link kelas wajib diisi"),
    }),
    onSubmit: async (values) => {
      try {
        const action = isEdit ? updateKelasOnline(currentKelas.id, values) : createKelasOnline(values);
        const response = await action;

        if (response.success) {
          toast.success(response.message || "Berhasil disimpan.");
          fetchData();
          toggleModal();
        } else {
          toast.error(response.message || "Gagal menyimpan kelas.");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Terjadi kesalahan.");
      }
    },
  });

  // --- Delete Handlers ---

  const handleDeleteClick = (kelas) => {
    setCurrentKelas(kelas);
    setDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (currentKelas?.id) {
        const response = await deleteKelasOnline(currentKelas.id);
        if (response.success) {
          toast.success("Kelas berhasil dihapus.");
          fetchData();
        } else {
          toast.error(response.message || "Gagal menghapus kelas.");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menghapus kelas.");
    } finally {
      setDeleteModal(false);
      setCurrentKelas(null);
    }
  };

  // --- Table Configuration ---

  const columns = useMemo(
    () => [
      { header: "Judul", accessorKey: "title", enableColumnFilter: false },
      { 
        header: "Tanggal", 
        accessorKey: "class_date", 
        enableColumnFilter: false,
        cell: (props) => {
          const date = new Date(props.getValue());
          if (isNaN(date.getTime())) {
            return props.getValue();
          }
          return date.toISOString().split('T')[0];
        }
      },
      { header: "Jam Mulai", accessorKey: "start_time", enableColumnFilter: false },
      { header: "Jam Selesai", accessorKey: "end_time", enableColumnFilter: false },
      {
        header: "Link",
        accessorKey: "url",
        enableColumnFilter: false,
        cell: (props) => (
          <a href={props.getValue()} target="_blank" rel="noreferrer">
            {props.getValue()}
          </a>
        ),
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        cell: (cellProps) => (
          <div className="hstack gap-2">
            <Button
              color="soft-info"
              size="sm"
              onClick={() => handleEditClick(cellProps.row.original)}
            >
              <i className="mdi mdi-pencil-outline" />
            </Button>
            <Button
              color="soft-danger"
              size="sm"
              onClick={() => handleDeleteClick(cellProps.row.original)}
            >
              <i className="mdi mdi-delete-outline" />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  // --- Custom Pagination Component ---

  const CustomPagination = () => {
    const { page, last_page, per_page, total } = pagination;

    if (total === 0) {
      return null;
    }

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(last_page, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i} active={i === page}>
          <PaginationLink onClick={() => fetchData(i, per_page)}>{i}</PaginationLink>
        </PaginationItem>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Menampilkan <span className="fw-semibold">{(page - 1) * per_page + 1}</span> sampai{" "}
          <span className="fw-semibold">{Math.min(page * per_page, total)}</span> dari{" "}
          <span className="fw-semibold">{total}</span> data
        </div>
        <Pagination className="pagination-rounded mb-0">
          <PaginationItem disabled={page === 1}>
            <PaginationLink previous onClick={() => page > 1 && fetchData(page - 1, per_page)}>
              &lt;
            </PaginationLink>
          </PaginationItem>
          {pages}
          <PaginationItem disabled={page === last_page}>
            <PaginationLink next onClick={() => page < last_page && fetchData(page + 1, per_page)}>
              &gt;
            </PaginationLink>
          </PaginationItem>
        </Pagination>
      </div>
    );
  };

  // --- Main Render Method ---

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleConfirmDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Kelas Online" breadcrumbItem="List Kelas Online" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center justify-content-between">
                    <h5 className="mb-0 card-title flex-grow-1">List Kelas Online</h5>
                    <Button color="primary" onClick={toggleModal}>
                      <i className="mdi mdi-plus me-1"></i> Tambah
                    </Button>
                  </div>
                </CardBody>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinners color="primary" />
                    </div>
                  ) : (
                    <>
                      <TableContainer
                        columns={columns}
                        data={data}
                        isGlobalFilter={false}
                        isPagination={false}
                        isColumnFilter={false}
                        tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                      />
                      <CustomPagination />
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={modal} toggle={toggleModal} centered>
            <ModalHeader toggle={toggleModal}>
              {isEdit ? "Edit Kelas Online" : "Tambah Kelas Online"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={validation.handleSubmit}>
                <Row>
                  <Col md="12" className="mb-3">
                    <Label htmlFor="title">Judul</Label>
                    <Input
                      id="title"
                      name="title"
                      type="text"
                      placeholder="Masukkan judul kelas"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.title || ""}
                      invalid={!!(validation.touched.title && validation.errors.title)}
                    />
                    <FormFeedback>{validation.errors.title}</FormFeedback>
                  </Col>
                  <Col md="12" className="mb-3">
                    <Label htmlFor="class_date">Tanggal</Label>
                    <Input
                      id="class_date"
                      name="class_date"
                      type="date"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.class_date || ""}
                      invalid={!!(validation.touched.class_date && validation.errors.class_date)}
                    />
                    <FormFeedback>{validation.errors.class_date}</FormFeedback>
                  </Col>
                  <Col md="6" className="mb-3">
                    <Label htmlFor="start_time">Jam Mulai</Label>
                    <Input
                      id="start_time"
                      name="start_time"
                      type="time"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.start_time || ""}
                      invalid={!!(validation.touched.start_time && validation.errors.start_time)}
                    />
                    <FormFeedback>{validation.errors.start_time}</FormFeedback>
                  </Col>
                  <Col md="6" className="mb-3">
                    <Label htmlFor="end_time">Jam Selesai</Label>
                    <Input
                      id="end_time"
                      name="end_time"
                      type="time"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.end_time || ""}
                      invalid={!!(validation.touched.end_time && validation.errors.end_time)}
                    />
                    <FormFeedback>{validation.errors.end_time}</FormFeedback>
                  </Col>
                  <Col md="12" className="mb-3">
                    <Label htmlFor="url">Link</Label>
                    <Input
                      id="url"
                      name="url"
                      type="url"
                      placeholder="e.g., https://meet.google.com/xyz-abc"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.url || ""}
                      invalid={!!(validation.touched.url && validation.errors.url)}
                    />
                    <FormFeedback>{validation.errors.url}</FormFeedback>
                  </Col>
                </Row>
                <div className="d-flex justify-content-end gap-2 mt-3">
                  <Button color="secondary" onClick={toggleModal}>
                    Batal
                  </Button>
                  <Button color="primary" type="submit">
                    {isEdit ? "Update" : "Simpan"}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </React.Fragment>
  );
};

export default KelasOnline;